import React, { useState, useEffect, FormEvent } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";

// Services & Types
import { AttendanceService } from "services/attendance.service";
import { MemberService } from "services/member.service";
import { EventService } from "services/event.service";
import {
  ISaveAttendance,
  IUpdateAttendance,
  IMemberResponseDTO,
  IEventResponse,
} from "models/church.types";

interface FormErrors {
  memberId?: string;
  eventId?: string;
}

export default function AttendanceRegister() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<{ value: number; label: string }[]>(
    [],
  );
  const [events, setEvents] = useState<{ value: number; label: string }[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});

  const [attendance, setAttendance] = useState<
    ISaveAttendance | IUpdateAttendance
  >({
    memberId: 0,
    eventId: 0,
    present: true,
    remarks: "",
  });

  // CONFIGURATION DU STYLE REACT-SELECT (Avec support du Portal global)
  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: "#0F172A",
      borderColor: state.isFocused
        ? "#D4AF37"
        : errors.memberId || errors.eventId
          ? "#ef4444"
          : "#334155",
      borderRadius: "0.5rem",
      padding: "3px",
      minHeight: "45px",
      color: "white",
      boxShadow: "none",
      transition: "all 0.2s",
      "&:hover": { borderColor: "#D4AF37" },
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: "#1E293B",
      border: "1px solid #334155",
      zIndex: 9999, // Augmenté pour la sécurité de superposition
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 9999, // Force l'index au niveau du body HTML
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? "#D4AF37" : "transparent",
      color: state.isFocused ? "#0F172A" : "white",
      padding: "10px",
      cursor: "pointer",
      fontSize: "0.875rem",
      "&:active": { backgroundColor: "#D4AF37" },
    }),
    singleValue: (base: any) => ({
      ...base,
      color: "white",
      fontSize: "0.875rem",
    }),
    input: (base: any) => ({ ...base, color: "white" }),
    placeholder: (base: any) => ({
      ...base,
      color: "#64748b",
      fontSize: "0.875rem",
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: "#475569",
      "&:hover": { color: "#D4AF37" },
    }),
    indicatorSeparator: () => ({ display: "none" }),
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Chargement des données de dictionnaire en parallèle via l'API mise à jour
        const [membersRes, eventsRes] = await Promise.all([
          MemberService.getAll(0, 1000),
          EventService.getAll(0, 1000),
        ]);

        setMembers(
          membersRes.data.map((m: IMemberResponseDTO) => ({
            value: m.id,
            label:
              `${m.lastName || ""} ${m.firstName || ""}`.trim() ||
              `Membre #${m.id}`,
          })),
        );

        setEvents(
          eventsRes.data.map((e: IEventResponse) => ({
            value: e.id,
            label: `${e.title} (${e.eventDate ? new Date(e.eventDate).toLocaleDateString("fr-FR") : "Date inconnue"})`,
          })),
        );

        if (isEditMode && id) {
          const data = await AttendanceService.getById(parseInt(id));
          setAttendance({
            id: data.id,
            memberId: data.memberId,
            eventId: data.eventId,
            present: data.present ?? true,
            remarks: data.remarks || "",
          });
        }
      } catch (error) {
        console.error("Erreur de chargement des ressources :", error);
        toast.error(
          "Erreur lors du chargement des dictionnaires et ressources",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validation locale
    const newErrors: FormErrors = {};
    if (!attendance.memberId || attendance.memberId === 0) {
      newErrors.memberId = "Veuillez sélectionner un membre";
    }
    if (!attendance.eventId || attendance.eventId === 0) {
      newErrors.eventId = "Veuillez sélectionner un événement";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...(isEditMode ? { id: (attendance as IUpdateAttendance).id } : {}),
        memberId: Number(attendance.memberId),
        eventId: Number(attendance.eventId),
        present: attendance.present,
        remarks: attendance.remarks?.trim() || null,
      };

      if (isEditMode) {
        await AttendanceService.update(payload as IUpdateAttendance);
        toast.success("Enregistrement de présence mis à jour");
      } else {
        await AttendanceService.create(payload as ISaveAttendance);
        toast.success("Présence enregistrée avec succès");
      }
      history.push("/admin/attendances");
    } catch (error: any) {
      console.error("Erreur d'enregistrement :", error);
      if (error.response?.status === 400 || error.response?.status === 409) {
        toast.error(
          "Conflit : Cet enregistrement de présence existe déjà (doublon).",
        );
      } else {
        toast.error("Erreur technique lors du traitement de la présence.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center mt-16 p-4 w-full min-h-screen">
      <div className="w-full max-w-2xl">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 rounded-xl bg-[#1E293B] border border-slate-700 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-[#0F172A] px-6 py-4 border-b border-slate-700">
            <div className="flex justify-between items-center">
              <h6 className="text-white text-sm font-bold uppercase tracking-widest">
                <i className="fas fa-user-check mr-2 text-[#D4AF37]"></i>
                {isEditMode
                  ? "Éditer l'enregistrement"
                  : "Marquer une présence"}
              </h6>
              <button
                type="button"
                onClick={() => history.goBack()}
                className="text-slate-400 hover:text-white text-xs font-bold uppercase transition-all"
              >
                <i className="fas fa-times mr-1"></i> Fermer
              </button>
            </div>
          </div>

          <div className="flex-auto px-6 py-8">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6">
                {/* Sélection de l'Événement avec Recherche */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-calendar-alt mr-2"></i>Programme de
                    l'église
                  </label>
                  <Select
                    options={events}
                    styles={customSelectStyles}
                    placeholder="Chercher un programme..."
                    value={
                      events.find((opt) => opt.value === attendance.eventId) ||
                      null
                    }
                    onChange={(selected) => {
                      setAttendance((prev) => ({
                        ...prev,
                        eventId: selected?.value || 0,
                      }));
                      if (errors.eventId)
                        setErrors((prev) => ({ ...prev, eventId: undefined }));
                    }}
                    noOptionsMessage={() => "Aucun événement trouvé"}
                    menuPortalTarget={document.body} // Évite d'être masqué par la carte
                  />
                  {errors.eventId && (
                    <p className="text-red-500 text-[10px] mt-1 italic uppercase tracking-tighter">
                      {errors.eventId}
                    </p>
                  )}
                </div>

                {/* Sélection du Membre avec Recherche */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-user mr-2"></i>Nom du membre
                  </label>
                  <Select
                    options={members}
                    styles={customSelectStyles}
                    placeholder="Taper le nom du membre..."
                    value={
                      members.find(
                        (opt) => opt.value === attendance.memberId,
                      ) || null
                    }
                    onChange={(selected) => {
                      setAttendance((prev) => ({
                        ...prev,
                        memberId: selected?.value || 0,
                      }));
                      if (errors.memberId)
                        setErrors((prev) => ({ ...prev, memberId: undefined }));
                    }}
                    noOptionsMessage={() => "Aucun membre trouvé"}
                    menuPortalTarget={document.body} // Évite d'être masqué par la carte
                  />
                  {errors.memberId && (
                    <p className="text-red-500 text-[10px] mt-1 italic uppercase tracking-tighter">
                      {errors.memberId}
                    </p>
                  )}
                </div>

                {/* Gestion Dynamique du Statut Présent / Absent */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-toggle-on mr-2"></i>Statut de présence
                  </label>
                  <div className="flex items-center gap-4 bg-[#0F172A] p-4 rounded-lg border border-slate-700">
                    <button
                      type="button"
                      onClick={() =>
                        setAttendance((prev) => ({ ...prev, present: true }))
                      }
                      className={`flex-1 py-2.5 rounded-lg font-black text-xs uppercase tracking-wider transition-all border ${
                        attendance.present
                          ? "bg-green-500/10 text-green-400 border-green-500/40 shadow-inner"
                          : "bg-transparent text-slate-500 border-transparent hover:text-slate-300"
                      }`}
                    >
                      <i className="fas fa-check-circle mr-2"></i>Présent
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setAttendance((prev) => ({ ...prev, present: false }))
                      }
                      className={`flex-1 py-2.5 rounded-lg font-black text-xs uppercase tracking-wider transition-all border ${
                        !attendance.present
                          ? "bg-red-500/10 text-red-400 border-red-500/40 shadow-inner"
                          : "bg-transparent text-slate-500 border-transparent hover:text-slate-300"
                      }`}
                    >
                      <i className="fas fa-times-circle mr-2"></i>Absent
                    </button>
                  </div>
                </div>

                {/* Champ Remarques */}
                <div>
                  <label className="text-[10px] text-[#D4AF37] uppercase font-black tracking-widest ml-1 mb-2 block">
                    <i className="fas fa-comment-alt mr-2"></i>Remarques /
                    Observations
                  </label>
                  <textarea
                    name="remarks"
                    value={attendance.remarks || ""}
                    onChange={(e) =>
                      setAttendance((prev) => ({
                        ...prev,
                        remarks: e.target.value,
                      }))
                    }
                    placeholder="Ex: En retard, Retenue professionnelle, Absent justifié..."
                    className="w-full bg-[#0F172A] border border-slate-700 rounded-lg px-4 py-3 text-white text-sm focus:border-[#D4AF37] outline-none transition-all window-control resize-none h-24"
                  />
                </div>
              </div>

              {/* Barre d'Actions */}
              <div className="mt-10 flex justify-end gap-4 border-t border-slate-700/50 pt-6">
                <button
                  type="button"
                  onClick={() => history.push("/admin/attendances")}
                  className="px-6 py-3 rounded-lg font-bold text-xs uppercase text-slate-400 hover:text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-[#D4AF37] text-[#0F172A] px-10 py-3 rounded-lg font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg hover:shadow-[#D4AF37]/20 disabled:opacity-50"
                >
                  {loading ? (
                    <i className="fas fa-spinner animate-spin mr-2"></i>
                  ) : (
                    <i className="fas fa-save mr-2"></i>
                  )}
                  {isEditMode ? "Mettre à jour" : "Valider le pointage"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
